/**
 * Crown01 — First-Time Buyer offer bar + email-capture modal.
 *
 * Replaces the static announcement bar. The visible site
 * never reveals the discount code — instead, the user enters their email and
 * the code is sent via Resend on the backend.
 *
 * Rendered via React Portal to document.body so it always sits above any
 * stacking context (sticky headers, product image z-indexes, hero collages).
 *
 * Black + gold Crown01 branding. Mobile-first. localStorage flag prevents
 * repeat prompts once the user has claimed their code.
 */
import React, { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import axios from "axios";

const STORAGE_KEY = "crown01_first_time_claim_v1";
const EXPIRY_DAYS = 365; // keep the "claimed" flag long enough to never re-prompt repeat customers

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

function loadClaim() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !obj.claimed_at) return null;
    const ageDays = (Date.now() - obj.claimed_at) / (1000 * 60 * 60 * 24);
    if (ageDays > EXPIRY_DAYS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return obj;
  } catch (e) {
    return null;
  }
}

function persistClaim(email) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ email, claimed_at: Date.now() }));
  } catch (e) { /* noop */ }
}

export default function FirstTimeOfferBar() {
  const [claimed, setClaimed] = useState(null); // {email, claimed_at} when stored
  const [modalOpen, setModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const emailInputRef = useRef(null);
  const previousActiveRef = useRef(null);

  // Hydrate on mount
  useEffect(() => {
    setClaimed(loadClaim());
  }, []);

  const openModal = useCallback(() => {
    setError("");
    setSuccess(false);
    setEmail("");
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  // Lock body scroll when modal is open + focus the email input + close on Escape
  useEffect(() => {
    if (!modalOpen) return;
    previousActiveRef.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Focus input on next tick (only when in the email-capture state)
    setTimeout(() => {
      if (emailInputRef.current) emailInputRef.current.focus();
    }, 40);
    const onKey = (e) => { if (e.key === "Escape") closeModal(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
      if (previousActiveRef.current && previousActiveRef.current.focus) {
        try { previousActiveRef.current.focus(); } catch (e) { /* noop */ }
      }
    };
  }, [modalOpen, closeModal]);

  const onSubmit = useCallback(async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value || !EMAIL_RE.test(value)) {
      setError("Please enter a valid email address.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/offer/first-time`, { email: value });
      if (res.data && res.data.ok) {
        persistClaim(value);
        setClaimed({ email: value, claimed_at: Date.now() });
        setSuccess(true);
      } else {
        setError("Something went wrong. Please try again in a moment.");
      }
    } catch (err) {
      const msg = err && err.response && err.response.data && err.response.data.detail;
      setError(typeof msg === "string" ? msg : "Couldn't send right now — please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  }, [email]);

  // -------- Top bar --------
  const Bar = (
    <div className="text-white text-xs md:text-sm relative" style={{ background: "var(--c-black)" }} data-testid="announcement-bar">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-2.5 flex items-center justify-between gap-3 md:gap-4">
        {/* Left trust strip — desktop only */}
        <div className="hidden md:flex items-center gap-5 min-w-0">
          <span className="inline-flex items-center gap-2"><i className="fa-solid fa-truck-fast" style={{ color: "var(--c-gold)" }}></i> Fast UK Delivery</span>
          <span className="inline-flex items-center gap-2"><i className="fa-solid fa-shield-halved" style={{ color: "var(--c-gold)" }}></i> Secure Checkout</span>
          <span className="inline-flex items-center gap-2"><i className="fa-solid fa-boxes-stacked" style={{ color: "var(--c-gold)" }}></i> Pack or Full Box</span>
        </div>

        {/* First-time offer trigger */}
        <button
          type="button"
          onClick={openModal}
          className="group flex-1 md:flex-none flex items-center justify-center gap-2 md:gap-2.5 text-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--c-gold)] rounded-full px-2 py-0.5"
          data-testid="first-time-offer-trigger"
          aria-label={claimed ? "Your Crown01 first-time offer has been emailed to you. Tap to re-open." : "First-time buyer offer — unlock your code by email"}
        >
          <span className="inline-flex items-center gap-1.5 text-[color:var(--c-gold)] font-semibold whitespace-nowrap">
            <i className="fa-solid fa-envelope-open-text mystery-pulse" aria-hidden="true"></i>
            <span className="hidden sm:inline">First-time buyer offer</span>
            <span className="sm:hidden">First-time offer</span>
          </span>
          <span className="hidden md:inline opacity-70">·</span>
          {claimed ? (
            <span className="inline-flex items-center gap-1.5">
              <span className="hidden md:inline">Code emailed to {claimed.email.length > 28 ? claimed.email.slice(0, 24) + "…" : claimed.email}</span>
              <span className="inline md:hidden text-[10px] uppercase tracking-wider underline">Code sent — view</span>
            </span>
          ) : (
            <>
              <span className="hidden md:inline">unlock your code by email</span>
              <span className="inline md:hidden text-[10px] uppercase tracking-wider underline decoration-[color:var(--c-gold)]/60">Unlock by email</span>
            </>
          )}
        </button>

        {/* Right link — desktop only */}
        <div className="hidden md:flex items-center gap-4 min-w-0">
          <Link className="hover:text-[color:var(--c-gold)] transition" to="/trade" data-testid="announcement-trade-link">
            <i className="fa-solid fa-handshake mr-1"></i> Trade &amp; Wholesale Pricing
          </Link>
        </div>
      </div>
    </div>
  );

  // -------- Modal (portaled to document.body to escape ALL stacking contexts) --------
  const Modal = modalOpen ? (
    <div
      className="fixed inset-0 z-[2147483647] flex items-start sm:items-center justify-center overflow-y-auto py-6 px-4"
      style={{ isolation: "isolate" }}
      data-testid="first-time-offer-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="first-time-offer-title"
    >
      {/* Dark backdrop */}
      <button
        type="button"
        aria-label="Close offer"
        onClick={closeModal}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm cursor-default"
        data-testid="first-time-offer-backdrop"
      />
      {/* Card */}
      <div
        className="relative w-full max-w-sm md:max-w-md rounded-2xl overflow-hidden shadow-2xl mystery-card-anim my-auto"
        style={{ background: "var(--c-black)", border: "1.5px solid var(--c-gold)" }}
      >
        {/* Gold top accent */}
        <div className="absolute inset-x-0 top-0 h-1" style={{ background: "linear-gradient(90deg, transparent, var(--c-gold), transparent)" }}></div>

        {/* Close button */}
        <button
          type="button"
          onClick={closeModal}
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 min-w-[36px] rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition z-10"
          data-testid="first-time-offer-close"
        >
          <i className="fa-solid fa-xmark"></i>
        </button>

        <div className="px-6 pt-10 pb-7">
          {/* Eyebrow */}
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold mb-2 text-center" style={{ color: "var(--c-gold)" }}>
            <i className="fa-solid fa-crown mr-1.5"></i>First-time buyer
          </div>

          {/* Icon */}
          <div className="text-center mystery-pop">
            <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full mb-4"
                 style={{ background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.35)" }}>
              <i className={`fa-solid ${success ? "fa-circle-check" : "fa-envelope-open-text"} text-3xl md:text-4xl`} style={{ color: "var(--c-gold)" }}></i>
            </div>
          </div>

          {!success ? (
            <>
              <h2 id="first-time-offer-title" className="font-display text-2xl md:text-3xl text-white text-center leading-tight" data-testid="first-time-offer-title">
                Unlock your first Crown01 offer
              </h2>
              <p className="text-sm md:text-[15px] text-white/70 mt-2 text-center leading-relaxed">
                Enter your email and we&apos;ll send your first-time buyer discount code straight to your inbox.
              </p>

              <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-3" noValidate>
                <div>
                  <label htmlFor="first-time-email" className="sr-only">Email address</label>
                  <input
                    ref={emailInputRef}
                    id="first-time-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (error) setError(""); }}
                    className="w-full rounded-full px-5 py-3.5 text-base text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[color:var(--c-gold)]"
                    style={{ background: "rgba(255,255,255,0.06)", border: "1.5px solid rgba(212,175,55,0.4)" }}
                    data-testid="first-time-offer-email"
                    required
                    disabled={submitting}
                  />
                </div>
                {error && (
                  <div className="text-[13px] px-1" style={{ color: "#FF9F87" }} data-testid="first-time-offer-error" role="alert">
                    <i className="fa-solid fa-triangle-exclamation mr-1.5"></i>{error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="rounded-full px-5 py-3.5 font-semibold text-sm md:text-base transition-transform hover:scale-[1.01] active:scale-[.99] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={{ background: "var(--c-gold)", color: "var(--c-black)" }}
                  data-testid="first-time-offer-submit"
                >
                  {submitting ? (
                    <><i className="fa-solid fa-circle-notch fa-spin"></i> Sending…</>
                  ) : (
                    <><i className="fa-solid fa-paper-plane"></i> Send My Code</>
                  )}
                </button>
                <p className="text-[11px] text-white/45 text-center leading-relaxed mt-1">
                  One-time, first-time buyers only. No spam — see our <Link to="/privacy" onClick={closeModal} className="underline" style={{ color: "rgba(212,175,55,0.7)" }}>Privacy Policy</Link>.
                </p>
              </form>
            </>
          ) : (
            <div className="mystery-fade-in text-center" data-testid="first-time-offer-success">
              <h2 className="font-display text-2xl md:text-3xl text-white leading-tight">
                Your code has been sent.
              </h2>
              <p className="text-sm md:text-[15px] text-white/75 mt-3 leading-relaxed">
                Please check your inbox — your first-time buyer discount code from Crown01 is on its way. Apply it at checkout to unlock your offer.
              </p>
              <p className="text-[12px] text-white/45 mt-3">
                Not seeing it? Check your spam folder, or email <a href="mailto:info@crown01.co.uk" className="underline" style={{ color: "rgba(212,175,55,0.85)" }}>info@crown01.co.uk</a>.
              </p>

              <div className="mt-6 flex flex-col gap-2.5">
                <Link
                  to="/shop"
                  onClick={closeModal}
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full font-semibold text-sm w-full"
                  style={{ background: "var(--c-red)", color: "#fff" }}
                  data-testid="first-time-offer-shop"
                >
                  Start shopping <i className="fa-solid fa-arrow-right"></i>
                </Link>
                <button
                  type="button"
                  onClick={closeModal}
                  className="text-xs uppercase tracking-[0.18em] font-semibold text-white/70 hover:text-white py-1"
                  data-testid="first-time-offer-dismiss"
                >
                  Continue browsing
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="px-6 py-3 text-center text-[10px] uppercase tracking-[0.18em]" style={{ background: "rgba(212,175,55,0.08)", color: "var(--c-gold)" }}>
          <i className="fa-solid fa-crown mr-1.5"></i>Crown01 · UK Supplier
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      {Bar}
      {typeof document !== "undefined" && Modal ? createPortal(Modal, document.body) : null}
    </>
  );
}
